import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Badge, Button, Col, Input, Label, Row } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import ButtonTooltip from "components/Common/ButtonTooltip"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import DuplicateCheckModal from "components/Common/DuplicateCheckModal"
import PageWrapper from "components/Common/PageWrapper"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import {
  useQuotations,
  useDeleteQuotation,
  useDuplicateQuotation,
  useUpdateQuotationStatus,
} from "hooks/useQuotation"
import { useAllQuotationStatuses } from "hooks/useQuotationStatus"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import {
  getQuotationExistingReceipts,
  getQuotationSimilar,
} from "api/quotationApi"
import { useCreateMoneyReceiptFromQuotation } from "hooks/useMoneyReceipt"
import { useToastMessage } from "hooks/useToastMessage"
import CustomerLink from "components/Common/CustomerLink"
import VehicleLink from "components/Common/VehicleLink"

const getVehicleInfo = (specs = [], fieldName) =>
  specs?.find(item => item?.key === fieldName)?.value ?? "-"

const DEFAULT_DELIVERY_STATUS = "not_delivered"

const DeliveryBadge = ({ isDelivered }) =>
  isDelivered ? <Badge color="success">Delivered</Badge> : null

const getDeliveryMovementInfo = item => {
  const movement = item?.vehicle?.customer_delivery_movement

  if (!movement) return ""

  return [
    movement?.movement_date
      ? formatReadableDate(movement.movement_date, "MMM d, y")
      : "",
    movement?.location_to?.name ? `to ${movement.location_to.name}` : "",
  ]
    .filter(Boolean)
    .join(" ")
}

const QuotationList = () => {
  document.title = "Quotation List | Aurobil Cars"

  const navigate = useNavigate()
  const location = useLocation()
  const { handleResponse, handleError } = useToastMessage()
  const { params, setPage, setSearch, updateParams, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
      delivery_status: DEFAULT_DELIVERY_STATUS,
    })

  const focusId = params?.q_focus ?? null
  const focusRowRef = useRef(null)

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      search: params.search ?? "",
      delivery_status:
        params.delivery_status === "all" ? "" : params.delivery_status ?? "",
    }),
    [params]
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    if (urlParams.get("delivery_status")) return

    updateParams({
      delivery_status: DEFAULT_DELIVERY_STATUS,
      page: params.page ?? 1,
    })
  }, [location.search, params.page, updateParams])

  const handleDeliveryStatusChange = event => {
    updateParams({
      delivery_status: event.target.value,
      page: 1,
    })
  }

  const { data, isLoading, refetch, isRefetching } = useQuotations(queryParams)
  const { mutateAsync: deleteQuotation, isPending: isDeleting } =
    useDeleteQuotation()
  const { mutateAsync: duplicateQuotation, isPending: isDuplicating } =
    useDuplicateQuotation()
  const { mutateAsync: createFromQuotation, isPending: creatingReceipt } =
    useCreateMoneyReceiptFromQuotation()
  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
    useUpdateQuotationStatus()

  const { data: statusData } = useAllQuotationStatuses()
  const statusIdBySlug = useMemo(() => {
    const raw = statusData?.data
    const items = Array.isArray(raw) ? raw : (raw?.data ?? [])
    return Object.fromEntries(items.map(s => [s.slug, s.id]))
  }, [statusData])

  const fetchedData = data?.data?.data ?? []
  const paginationInfo = data?.data

  const [downloadingId, setDownloadingId] = useState(null)
  const [checkingDuplicate, setCheckingDuplicate] = useState(false)
  const [duplicateCheckModal, setDuplicateCheckModal] = useState(null)
  const [deleteModal, setDeleteModal] = useState(null)
  const [confirmModal, setConfirmModal] = useState(null)

  useEffect(() => {
    if (!focusId) return
    const node = focusRowRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    node.classList.add("table-warning")
    const timer = setTimeout(() => {
      node.classList.remove("table-warning")
      updateParams({ q_focus: undefined })
    }, 2500)
    return () => clearTimeout(timer)
  }, [focusId, fetchedData, updateParams])

  const handleDownload = async (rowId, identifier) => {
    setDownloadingId(rowId)
    try {
      await downloadAndOpenFile({ url: `/quotations/${identifier}/pdf` })
    } catch (error) {
      handleError(error, "Failed to download quotation PDF.")
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDelete = async item => {
    try {
      const response = await deleteQuotation({ id: item.id })
      handleResponse(response, "Quotation deleted successfully.", true)
      setDeleteModal(null)
      refetch()
    } catch (error) {
      handleError(error, "Failed to delete quotation.")
    }
  }

  const doDuplicate = async item => {
    try {
      const response = await duplicateQuotation(item.id)
      handleResponse(response, "Quotation duplicated successfully.", true)
      const newId = response?.data?.qid ?? response?.data?.id
      if (newId) {
        navigate(`/quotation-list/${newId}/edit`, { state: { from: location } })
      } else {
        refetch()
      }
    } catch (error) {
      handleError(error, "Failed to duplicate quotation.")
    }
  }

  const handleDuplicate = async item => {
    setCheckingDuplicate(true)
    try {
      const res = await getQuotationSimilar(item.id)
      const existing = res?.data?.items ?? []
      if (existing.length > 0) {
        setDuplicateCheckModal({
          type: "quotation",
          item,
          existingItems: existing.map(q => ({
            id: q.id,
            label: q.qid ?? `Q #${q.id}`,
            subLabel: `Status: ${q.status?.name ?? "Pending"} · ${formatReadableDate(q.created_at, "MMM d, y")}`,
            linkTo: `/quotation-list/${q.qid ?? q.id}/view`,
          })),
        })
        return
      }
    } catch {
      // on check failure, proceed silently
    } finally {
      setCheckingDuplicate(false)
    }
    await doDuplicate(item)
  }

  const doCreateReceipt = async item => {
    try {
      const response = await createFromQuotation({
        quotation_id: item.id,
        issued_date: new Date().toISOString().split("T")[0],
      })
      const moneyReceiptId = response?.data?.id
      if (moneyReceiptId) {
        navigate(`/money-receipts/${moneyReceiptId}`)
      }
    } catch (error) {
      handleError(error, "Failed to create money receipt from quotation.")
    }
  }

  const handleCreateReceipt = async item => {
    setCheckingDuplicate(true)
    try {
      const res = await getQuotationExistingReceipts(item.id)
      const existing = res?.data?.items ?? []
      if (existing.length > 0) {
        setDuplicateCheckModal({
          type: "receipt",
          item,
          existingItems: existing.map(mr => ({
            id: mr.id,
            label: mr.mr_no ?? `MR #${mr.id}`,
            subLabel: `Issued: ${formatReadableDate(mr.issued_date, "MMM d, y")}`,
            linkTo: `/money-receipts/${mr.id}`,
          })),
        })
        return
      }
    } catch {
      // on check failure, proceed silently
    } finally {
      setCheckingDuplicate(false)
    }
    await doCreateReceipt(item)
  }

  const handleStatusChange = async (item, slug) => {
    const quotation_status_id = statusIdBySlug[slug]
    if (!quotation_status_id) return
    try {
      const response = await updateStatus({ id: item.id, quotation_status_id })
      handleResponse(
        response,
        `Quotation ${slug === "approved" ? "accepted" : "declined"} successfully.`,
        true
      )
    } catch (error) {
      handleError(error, `Failed to ${slug === "approved" ? "accept" : "decline"} quotation.`)
    }
  }

  const handleConfirmAction = async () => {
    const { type, item } = confirmModal
    setConfirmModal(null)
    if (type === "duplicate") {
      await handleDuplicate(item)
    } else if (type === "receipt") {
      await handleCreateReceipt(item)
    } else if (type === "approve") {
      await handleStatusChange(item, "approved")
    } else if (type === "reject") {
      await handleStatusChange(item, "rejected")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Quotation",
        cell: ({ row }) => {
          const item = row.original
          const specs = item?.pdf_content?.vehicle_specs ?? []
          const status = item?.status
          const deliveryInfo = getDeliveryMovementInfo(item)
          const statusColor =
            status?.slug === "approved"
              ? "success"
              : status?.slug === "rejected"
              ? "danger"
              : "warning"

          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="d-flex align-items-center gap-1 flex-wrap mb-1">
                <Link to={`/quotation-list/${item?.qid ?? item?.id}/view`}>
                  {item?.qid ?? "-"}
                </Link>
                <DeliveryBadge isDelivered={item?.is_delivered} />
              </div>
              {deliveryInfo ? (
                <div className="small text-success">
                  Customer Delivery: {deliveryInfo}
                </div>
              ) : null}
              <div className="mb-1">
                <VehicleLink
                  vehicle={item.vehicle}
                  name={getVehicleInfo(specs, "Vehicle Name")}
                />
              </div>
              <small className="text-muted d-block">
                Stock:{" "}
                <VehicleLink
                  vehicle={item.vehicle}
                  name={item?.vehicle?.stock_id ?? "-"}
                />
              </small>
              <small className="text-muted d-block">
                Chassis: {getVehicleInfo(specs, "Chassis No")}
              </small>
              <div className="d-sm-none mt-1">
                <div className="fw-semibold">
                  <CustomerLink
                    customer={item.customer}
                    fallbackName={item?.customer?.name}
                  />
                </div>
                <small className="text-muted d-block">{item?.customer?.mobile ?? "-"}</small>
                <Badge color={statusColor} className="mt-1">
                  {status?.name ?? "Pending"}
                </Badge>
              </div>
            </div>
          )
        },
      },
      {
        header: "Customer",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="fw-semibold">
                <CustomerLink
                  customer={item.customer}
                  fallbackName={item?.customer?.name}
                />
              </div>
              <small className="text-muted d-block">{item?.customer?.mobile ?? "-"}</small>
              <small className="text-muted d-block">{item?.customer?.email ?? "-"}</small>
            </div>
          )
        },
      },
      {
        header: "Amount",
        meta: { style: { width: "22%" }, className: "d-none d-md-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          const priceItems = item?.pdf_content?.price_items ?? []
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              {priceItems.map((priceItem, index) => (
                <div
                  key={priceItem?.id ?? `price-${index}`}
                  className="small text-muted d-flex justify-content-between gap-2"
                >
                  <span className="text-truncate">
                    {priceItem?.description || "-"}
                  </span>
                  <span>{formatBDT(priceItem?.amount)}</span>
                </div>
              ))}
              <div className="fw-bold text-success border-top pt-1 mt-1 d-flex justify-content-between gap-2">
                <span>Total</span>
                <span>{formatBDT(item?.pdf_content?.total_amount)}</span>
              </div>
              <small className="text-muted d-block mt-1">
                Valid: {formatReadableDate(item?.pdf_content?.valid_until, "MMM d, y")}
              </small>
            </div>
          )
        },
      },
      {
        header: "Status",
        meta: { style: { width: "8%" }, className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const status = row.original?.status
          const color =
            status?.slug === "approved"
              ? "success"
              : status?.slug === "rejected"
              ? "danger"
              : "warning"

          return <Badge color={color}>{status?.name ?? "Pending"}</Badge>
        },
      },
      {
        header: "Date",
        meta: { style: { width: "12%" }, className: "d-none d-lg-table-cell" },
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
            <div>{formatReadableDate(row.original?.pdf_content?.quotation_date, "MMM d, y")}</div>
            <small className="text-muted d-block">
              Created: {formatReadableDate(row.original?.created_at, "MMM d, y")}
            </small>
          </div>
        ),
      },
      {
        header: "Action",
        meta: { className: "table-col-action" },
        cell: ({ row }) => {
          const item = row.original
          const isPending = item?.status?.slug === "pending"

          return (
            <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
              <li>
                <ButtonTooltip
                  type="view"
                  btnId={`quotation-view-${item.id}`}
                  onClick={() =>
                    navigate(`/quotation-list/${item?.qid ?? item?.id}/view`, {
                      state: { from: location },
                    })
                  }
                />
              </li>
              {isPending && (
                <>
                  <li>
                    <ButtonTooltip
                      type="accept"
                      btnId={`quotation-accept-${item.id}`}
                      loading={isUpdatingStatus}
                      onClick={() => setConfirmModal({ type: "approve", item })}
                    />
                  </li>
                  <li>
                    <ButtonTooltip
                      type="decline"
                      btnId={`quotation-decline-${item.id}`}
                      loading={isUpdatingStatus}
                      onClick={() => setConfirmModal({ type: "reject", item })}
                    />
                  </li>
                </>
              )}
              <li>
                <ButtonTooltip
                  type="edit"
                  btnId={`quotation-edit-${item.id}`}
                  onClick={() =>
                    navigate(`/quotation-list/${item?.qid ?? item?.id}/edit`, {
                      state: { from: location },
                    })
                  }
                />
              </li>
              <li>
                <ButtonTooltip
                  type="duplicate"
                  btnId={`quotation-duplicate-${item.id}`}
                  loading={isDuplicating || checkingDuplicate}
                  onClick={() => setConfirmModal({ type: "duplicate", item })}
                />
              </li>
              <li>
                <ButtonTooltip
                  type="pdf"
                  btnId={`quotation-pdf-${item.id}`}
                  loading={downloadingId === item.id}
                  onClick={() => handleDownload(item.id, item?.qid ?? item?.id)}
                />
              </li>
              <li>
                <ButtonTooltip
                  type="receipt"
                  btnId={`quotation-mr-${item.id}`}
                  loading={creatingReceipt || checkingDuplicate}
                  onClick={() => setConfirmModal({ type: "receipt", item })}
                />
              </li>
              <li>
                <ButtonTooltip
                  type="delete"
                  btnId={`quotation-delete-${item.id}`}
                  loading={isDeleting}
                  onClick={() => setDeleteModal(item)}
                />
              </li>
            </ul>
          )
        },
      },
    ],
    [
      navigate,
      location,
      creatingReceipt,
      isDeleting,
      isDuplicating,
      downloadingId,
      checkingDuplicate,
      isUpdatingStatus,
    ]
  )

  return (
    <PageWrapper title="Quotation" breadcrumbItem="Quotation List">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total{" "}
            <span className="badge badge-soft-info">{paginationInfo?.total ?? 0}</span>
          </h5>
        </Col>
        <Col xs={6} className="d-flex justify-content-end align-items-center gap-1">
          <ButtonTooltip
            type="refresh"
            btnId="refreshQuotationList"
            size={null}
            loading={isRefetching}
            onClick={() => refetch()}
          />
          <Button color="primary" onClick={() => navigate("/quotation-list/add")}>
            <i className="bx bx-plus" />
            <span className="d-none d-sm-inline ms-1">Add Quotation</span>
          </Button>
          {hasActiveFilters() && (
            <Button
              color="danger"
              onClick={() =>
                resetParams([], {
                  page: 1,
                  delivery_status: DEFAULT_DELIVERY_STATUS,
                })
              }
            >
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <Row className="my-3">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch
            value={params.search ?? ""}
            onChange={setSearch}
            placeholder="Search quotation, customer, stock..."
          />
        </Col>
        <Col xs={12} sm={6} md={3} lg={2}>
          <Label htmlFor="quotation-delivery-status" className="visually-hidden">
            Delivery Status
          </Label>
          <Input
            id="quotation-delivery-status"
            type="select"
            value={params.delivery_status ?? DEFAULT_DELIVERY_STATUS}
            onChange={handleDeliveryStatusChange}
          >
            <option value="all">All</option>
            <option value="delivered">Delivered</option>
            <option value="not_delivered">Not Delivered</option>
          </Input>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={fetchedData}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={queryParams.per_page}
        getRowKey={row => row.id}
        rowProps={row => {
          const isFocus =
            focusId &&
            (String(row.id) === String(focusId) ||
              String(row.qid) === String(focusId))
          if (!isFocus) return {}
          return { ref: focusRowRef }
        }}
      />

      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => handleDelete(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
        loading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!confirmModal}
        toggle={() => setConfirmModal(null)}
        title={
          {
            duplicate: "Duplicate Quotation?",
            receipt: "Create Money Receipt?",
            approve: "Accept Quotation?",
            reject: "Decline Quotation?",
          }[confirmModal?.type]
        }
        message={
          {
            duplicate: "This will create a copy of the quotation. Do you want to proceed?",
            receipt: "This will create a new money receipt from this quotation. Do you want to proceed?",
            approve: "This will mark the quotation as accepted. Do you want to proceed?",
            reject: "This will mark the quotation as declined. Do you want to proceed?",
          }[confirmModal?.type]
        }
        confirmLabel={
          {
            duplicate: "Duplicate",
            receipt: "Create",
            approve: "Accept",
            reject: "Decline",
          }[confirmModal?.type]
        }
        icon={
          {
            duplicate: "mdi-content-copy",
            receipt: "mdi-receipt",
            approve: "mdi-check-circle-outline",
            reject: "mdi-close-circle-outline",
          }[confirmModal?.type]
        }
        btnColor={
          { approve: "success", reject: "danger" }[confirmModal?.type] ?? "primary"
        }
        onConfirm={handleConfirmAction}
        isConfirming={isDuplicating || creatingReceipt || checkingDuplicate || isUpdatingStatus}
      />

      {duplicateCheckModal && (
        <DuplicateCheckModal
          isOpen={!!duplicateCheckModal}
          toggle={() => setDuplicateCheckModal(null)}
          title={
            duplicateCheckModal.type === "receipt"
              ? "Existing Money Receipts Found"
              : "Similar Quotations Found"
          }
          warningText={
            duplicateCheckModal.type === "receipt"
              ? `${duplicateCheckModal.existingItems.length} money receipt(s) already exist for this customer & vehicle.`
              : `${duplicateCheckModal.existingItems.length} similar quotation(s) found for this customer & vehicle.`
          }
          items={duplicateCheckModal.existingItems}
          proceedLabel={
            duplicateCheckModal.type === "receipt"
              ? "Create New Anyway"
              : "Duplicate Anyway"
          }
          onProceed={async () => {
            const { type, item } = duplicateCheckModal
            setDuplicateCheckModal(null)
            if (type === "receipt") {
              await doCreateReceipt(item)
            } else {
              await doDuplicate(item)
            }
          }}
          isProceeding={creatingReceipt || isDuplicating}
        />
      )}
    </PageWrapper>
  )
}

export default QuotationList
